import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import SellerSidebar from "./common/SellerSidebar";
import axiosClient from "../Services/Axios/axios";
import Swal from "sweetalert2";
import { ClipLoader } from "react-spinners";
import ProfileSidebar from "../pages/common/ProfileSidebar";

const AddProduct = ({ initialValues, categories, stockOptions, onSubmit }) => {
  /* ----------------------------- Default Values ----------------------------- */
  const defaults = useMemo(
    () => ({
      title: "",
      sku: "",
      inStock: "Yes",
      price: "",
      category: "",
      shortDescription: "",
      availableStock: "",
      thumbImage: "",
      images: [""],
      addVariant: false,
      variants: [], // selected variant IDs
    }),
    []
  );

  /* ----------------------------- State Management ---------------------------- */
  const [form, setForm] = useState({ ...defaults, ...(initialValues || {}) });
  const [errors, setErrors] = useState({});
  const [variantItems, setVariantItems] = useState([]); // variants list from API
  const [categoryItems, setCategoryItems] = useState([]); // variants list from API
  const [variantOptionsMap, setVariantOptionsMap] = useState({}); // options per variant
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  /* ---------------------------- Dropdown Options ----------------------------- */

  const stockItems = [
    { label: "Yes", value: 1 },
    { label: "No", value: 0 },
  ];

  /* ----------------------------- Handlers ----------------------------- */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Upload single file (thumbImage or multiple images)
  const uploadFile = async (file, onSuccess) => {
    try {
      const maxFileSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxFileSize) {
        Swal.fire({
          icon: "error",
          title: "File size exceeds 5MB",
          text: "Please upload a file smaller than 5MB.",
        });
        return;
      }

      const formData = new FormData();
      formData.append("image", file);

      const response = await axiosClient.post("users/uploadimg", formData);
      if (!response.data.error) {
        onSuccess(response.data.file);
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload failed",
          text: response.data.message || "Something went wrong!",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text: error.message || "Something went wrong!",
      });
    }
  };
  const handleImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      uploadFile(file, (uploadedFile) => {
        setForm((prev) => {
          const copy = [...prev.images];
          copy[index] = uploadedFile;
          return { ...prev, images: copy };
        });
      });
    }
  };

  const handleThumbChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadFile(file, (uploadedFile) => {
        setForm((prev) => ({ ...prev, thumbImage: uploadedFile }));
      });
    }
  };

  const addMoreImage = () =>
    setForm((prev) => ({ ...prev, images: [...prev.images, ""] }));

  const removeImage = (index) => {
    setForm((prev) => {
      const updated = [...prev.images];
      updated.splice(index, 1);
      return { ...prev, images: updated.length ? updated : [""] };
    });
  };

  // Variant select handler
  const handleVariantSelect = async (e) => {
    const variantId = e.target.value;
    if (!variantId) return;

    if (!form.variants.includes(variantId)) {
      setForm((prev) => ({
        ...prev,
        variants: [...prev.variants, variantId],
      }));

      // Fetch variant options
      try {
        const response = await axiosClient.get(
          `/products/variants-options/${variantId}`
        );
        if (response.status === 200) {
          setVariantOptionsMap((prev) => ({
            ...prev,
            [variantId]: response?.data?.data?.map((opt) => ({
              label: opt.value,
              value: opt.id,
            })),
          }));
        }
      } catch (error) {
        console.error("Error fetching variant options:", error);
      }
    }
  };

  // Remove variant
  const removeVariant = (variantId) => {
    setForm((prev) => {
      const updated = { ...prev };
      delete updated[`variant_${variantId}`]; // remove selected option
      return {
        ...updated,
        variants: prev.variants.filter((v) => v !== variantId),
      };
    });
  };

  // Handle variant option selection
  const handleVariantOptionChange = (variantId, optionId) => {
    setForm((prev) => ({
      ...prev,
      [`variant_${variantId}`]: optionId,
    }));
  };

  /* --------------------------- Validation --------------------------- */
  const validate = () => {
    const newErrors = {};

    if (!form.title.trim()) newErrors.title = "Product title is required";
    if (!form.sku.trim()) newErrors.sku = "SKU is required";

    if (!form.price) newErrors.price = "Price is required";
    else if (isNaN(form.price) || Number(form.price) <= 0)
      newErrors.price = "Enter a valid price";

    if (!form.category) newErrors.category = "Category is required";

    // ✅ Available stock validation
    if (!form.availableStock) {
      newErrors.availableStock = "Available stock is required";
    } else if (isNaN(form.availableStock)) {
      newErrors.availableStock = "Stock must be a number";
    } else if (String(form.availableStock).length > 5) {
      newErrors.availableStock = "Stock cannot exceed 5 digits";
    }

    if (!form.thumbImage) newErrors.thumbImage = "Thumb image is required";

    // ✅ Variant validation
    if (form.addVariant) {
      if (form.variants.length === 0) {
        newErrors.variants = "Please select at least one variant";
      } else {
        form.variants.forEach((vId) => {
          if (!form[`variant_${vId}`]) {
            newErrors[`variant_${vId}`] = "Please select an option";
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const addProductData = await axiosClient.post("products/add-product", {
        product_title: form.title,
        sku: form.sku,
        thumb_image: form.thumbImage,
        category_id: form.category,
        short_description: form.shortDescription,
        price: form.price,
        stock: form.availableStock,
        stock_availability_status: form.inStock,
        images: form.images,
        variants: form.variants.map(String),
        variant_options: form.variants
          .map((vId) => form[`variant_${vId}`])
          .filter(Boolean)
          .map(String),
      });
      if (!addProductData.data.error) {
        setLoading(false);
        Swal.fire({
          icon: "success",
          title: addProductData.data.message,
        });
        navigate("/seller-dashboard/product-list");
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  /* ----------------------------- API Calls ----------------------------- */
  const getVariantsListData = async () => {
    try {
      const response = await axiosClient.get("/products/variants");
      if (response.status === 200) {
        setVariantItems(
          response?.data?.data?.map((item) => ({
            label: item.name,
            value: String(item.id),
          }))
        );
      }

      const response1 = await axiosClient.get("/products/category");
      if (response1.status === 200) {
        // ✅ correct variable
        setCategoryItems(
          response1?.data?.data?.map((item) => ({
            label: item.name,
            value: String(item.id),
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching variants or categories:", error);
    }
  };

  useEffect(() => {
    getVariantsListData();
  }, []);

  /* ----------------------------- JSX ----------------------------- */
  return (
    <div className="all_suport_bg manages_top">
      <section className="ser_v_araeea profiles">
        <div className="container">
          <div className="bradcams mb_view_hide">
            <span className="bothss">
              <Link to="">My Account</Link>{" "}
              <img src="/images/brd_rowwo.png" alt="" /> Shop
              <img src="/images/brd_rowwo.png" alt="" /> Product
              <img src="/images/brd_rowwo.png" alt="" /> Add Product
            </span>
          </div>

          <div className="row">
            <div className="col-lg-3 mb_view_hide">
              <SellerSidebar />
            </div>

            <div className="col-lg-9 add-products">
              <h4>Add Products</h4>

              <div className="form_boxx profilss">
                <div className="form_arar mt-0">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      {/* Product Title */}
                      <div className="col-lg-12">
                        <div className="form-group add-product-title">
                          <label>Product Title</label>
                          <input
                            type="text"
                            className="upload-control"
                            name="title"
                            value={form.title}
                            placeholder="Enter product title"
                            onChange={handleChange}
                          />
                          {errors.title && (
                            <p className="error-text">{errors.title}</p>
                          )}

                          <div className="row">
                            {/* SKU */}
                            <div className="col-lg-6">
                              <div className="form-group">
                                <label>SKU</label>
                                <input
                                  type="text"
                                  className="upload-control"
                                  name="sku"
                                  value={form.sku}
                                  placeholder="Enter sku"
                                  onChange={handleChange}
                                />
                                {errors.sku && (
                                  <p className="error-text">{errors.sku}</p>
                                )}
                              </div>
                            </div>

                            {/* Stock Availability */}
                            <div className="col-lg-6">
                              <div className="form-group">
                                <label>Stock Availability</label>
                                <select
                                  className="upload-control"
                                  name="inStock"
                                  value={form.inStock}
                                  onChange={handleChange}
                                >
                                  {stockItems.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Price */}
                            <div className="col-lg-6">
                              <div className="form-group">
                                <label>Price</label>
                                <input
                                  type="text"
                                  className="upload-control"
                                  name="price"
                                  value={form.price}
                                  placeholder="Enter price"
                                  onChange={handleChange}
                                />
                                {errors.price && (
                                  <p className="error-text">{errors.price}</p>
                                )}
                              </div>
                            </div>

                            {/* Category */}
                            <div className="col-lg-6">
                              <div className="form-group">
                                <label>Category</label>
                                <select
                                  className="upload-control"
                                  name="category"
                                  value={form.category}
                                  onChange={handleChange}
                                >
                                  <option value="">Select Category</option>
                                  {categoryItems.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                                {errors.category && (
                                  <p className="error-text">
                                    {errors.category}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Short Description */}
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="form-group">
                          <label>Short Description</label>
                          <textarea
                            className="upload-control"
                            rows={5}
                            name="shortDescription"
                            value={form.shortDescription}
                            onChange={handleChange}
                          ></textarea>
                        </div>
                      </div>

                      {/* Stock */}
                      <div className="col-lg-6">
                        <div className="form-group">
                          <label>Available stock</label>
                          <input
                            type="number"
                            className="upload-control"
                            name="availableStock"
                            value={form.availableStock}
                            placeholder="Enter available stock"
                            onChange={handleChange}
                            onInput={(e) => {
                              if (e.target.value.length > 5) {
                                e.target.value = e.target.value.slice(0, 5); // only 5 digits allowed
                              }
                            }}
                          />
                          {errors.availableStock && (
                            <p className="error-text">
                              {errors.availableStock}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Thumb Image */}
                      <div className="col-lg-6">
                        <div className="form-group">
                          <label>Thumb Image</label>
                          <label className="upload-input upload-control">
                            {form.thumbImage ? (
                              <img
                                src={form.thumbImage} // yaha pe uploaded image ka URL set hoga
                                alt="Thumb Preview"
                                style={{
                                  maxWidth: "120px",
                                  maxHeight: "120px",
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <span className="upload-placeholder">
                                Choose Thumb Image
                              </span>
                            )}

                            <input
                              type="file"
                              name="thumbImage"
                              accept="image/*"
                              onChange={handleThumbChange}
                            />
                            <img
                              className="upload-icon"
                              src="/images/pin.png"
                              alt="attach"
                            />
                          </label>

                          {errors.thumbImage && (
                            <p className="error-text">{errors.thumbImage}</p>
                          )}
                        </div>
                      </div>

                      {/* Multiple Images */}
                      <div className="col-lg-12">
                        <label>Images</label>
                        {form.images.map((img, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center"
                            style={{ gap: "10px", marginBottom: "8px" }}
                          >
                            <label
                              className="upload-input upload-control"
                              style={{ flex: 1 }}
                            >
                              {img ? (
                                <img
                                  src={img} // API se aaya hua ya set kiya hua image ka URL
                                  alt={`Uploaded ${idx + 1}`}
                                  style={{
                                    maxWidth: "120px",
                                    maxHeight: "120px",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <span className="upload-placeholder">
                                  {`Choose Image ${idx + 1}`}
                                </span>
                              )}

                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange(idx, e)}
                              />
                              <img
                                className="upload-icon"
                                src="/images/pin.png"
                                alt="attach"
                              />
                            </label>

                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="btn-remove-product"
                              style={{
                                background: "red",
                                color: "white",
                                border: "none",
                                borderRadius: "5px",
                                padding: "5px 10px",
                                cursor: "pointer",
                              }}
                            >
                              ×
                            </button>

                            {idx === form.images.length - 1 && (
                              <button
                                type="button"
                                onClick={addMoreImage}
                                className="btn-add-product"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                <span className="plus">+</span> Add More
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Add Variant */}
                      <div className="col-lg-12">
                        <div
                          className="form-group d-flex align-items-center"
                          style={{ gap: "10px" }}
                        >
                          <label style={{ marginBottom: 0 }}>Add Variant</label>
                          <input
                            type="checkbox"
                            name="addVariant"
                            checked={form.addVariant}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      {/* Variant Dropdown */}
                      {form.addVariant && (
                        <div className="col-lg-12">
                          <div className="form-group">
                            <label>Select Variant</label>
                            <select
                              className="upload-control"
                              onChange={handleVariantSelect}
                            >
                              <option value="">Select Variant</option>
                              {variantItems.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            {errors.variants && (
                              <p className="error-text">{errors.variants}</p>
                            )}
                            <div
                              className="variant-tags mt-2"
                              style={{
                                display: "flex",
                                gap: "10px",
                                flexWrap: "wrap",
                              }}
                            >
                              {form.variants.map((vId) => {
                                const v = variantItems.find(
                                  (vi) => vi.value === vId
                                );
                                return (
                                  <span key={vId} className="variant-tag">
                                    {v?.label}
                                    <b
                                      style={{
                                        cursor: "pointer",
                                        marginLeft: "5px",
                                      }}
                                      onClick={() => removeVariant(vId)}
                                    >
                                      ×
                                    </b>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Variant Option Dropdowns */}
                      {form.variants.map((variantId) => {
                        const options = variantOptionsMap[variantId] || [];
                        const variant = variantItems.find(
                          (vi) => vi.value === variantId
                        );
                        return (
                          <div key={variantId} className="col-lg-12">
                            <div className="form-group">
                              <label>{variant?.label}</label>
                              <select
                                className="upload-control"
                                value={form[`variant_${variantId}`] || ""}
                                onChange={(e) =>
                                  handleVariantOptionChange(
                                    variantId,
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">
                                  Choose {variant?.label}
                                </option>
                                {options.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                              {errors[`variant_${variantId}`] && (
                                <p className="error-text">
                                  {errors[`variant_${variantId}`]}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Submit */}
                      <div className="col-lg-12 text-center">
                        <button
                          type="submit"
                          className="btn-add-product"
                          disabled={loading}
                        >
                          {loading ? (
                            <ClipLoader color="#fff" size={"15"} />
                          ) : (
                            "Add Product"
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddProduct;
